import React, { createContext, useContext, useState, useEffect } from "react";
import Cookies from 'universal-cookie';
import { GRAPHQL_ENDPOINTS } from '../../config/api';
import { getAuthToken } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import {
  extractAndStoreCsrfToken,
  getCsrfTokenSync,
  getCsrfTokenKeyByEnv,
  clearCsrfToken,
} from '../../utils/csrf';

// HTTP status codes returned by the backend in either GraphQL `errors[].extensions`
// or inside the response payload itself (e.g. `checkSession.statusCode`).
const STATUS = {
  OK: 'OK',
  UNAUTHORIZED: 401,
  EXPIRED: 498,
};

const AuthContext = createContext({
  isAuthenticated: false,
  isLoading: true,
  checkAuthStatus: () => {},
  setIsAuthenticated: () => {},
  redirectToLogin: () => {},
  requireAuth: () => {},
});

export const redirectToLogin = () => {
  window.location.href = '/';
};

// Call the backend to refresh the access token using the refresh-token cookie.
// Returns true if a new access token was successfully stored.
export const refreshAccessToken = async () => {
  try {
    const csrfToken = getCsrfTokenSync();
    const csrfTokenKey = getCsrfTokenKeyByEnv();
    const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
        ...(csrfToken ? { [csrfTokenKey]: csrfToken } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({
        query: `query Refresh {
          refreshAccessToken {
            status
            statusCode
            errorCode
            message
            data { accessToken }
          }
        }`,
        variables: {},
      }),
    });
    // Capture rotated CSRF token if the server issues a new one
    extractAndStoreCsrfToken(response);

    let json = null;
    try {
      json = await response.json();
    } catch {
      json = null;
    }
    const refresh = json?.data?.refreshAccessToken;
    if (refresh?.status === STATUS.OK) {
      const accessToken = refresh?.data?.accessToken;
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('AuthContext: Error refreshing token:', error);
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const clearAuthState = () => {
    console.log('AuthContext: Clearing authentication state');
    localStorage.removeItem('accessToken');
    // Don't remove preventAutoLogin flag - let it persist to prevent auto-login after logout
    
    // Clear CSRF token cookie
    clearCsrfToken();

    setIsAuthenticated(false);
  };

  // Call the backend `logout` mutation, then clear local auth state.
  // Mirrors the admin-portal flow. Always clears local state even if the
  // network call fails so the user is signed out client-side.
  const logoutUser = async () => {
    try {
      const csrfToken = getCsrfTokenSync();
      const csrfTokenKey = getCsrfTokenKeyByEnv();
      const token = getAuthToken();
      const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(csrfToken ? { [csrfTokenKey]: csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          query: `mutation Logout {
            logout {
              status
              statusCode
              errorCode
              message
            }
          }`,
          variables: {},
        }),
      });
      let json = null;
      try {
        json = await response.json();
      } catch {
        json = null;
      }
      console.log('AuthContext: Logout response', json?.data?.logout);
    } catch (error) {
      console.error('AuthContext: Logout request failed:', error);
    } finally {
      clearAuthState();
    }
  };

  const checkAuthStatus = async (isRetryAfterTokenRefresh = false) => {
    try {
      // Check prevent flags first - if set, don't do anything
      const preventAutoLogin = localStorage.getItem('preventAutoLogin') === 'true';
      const sessionPreventAutoLogin = sessionStorage.getItem('preventAutoLogin') === 'true';
      
      if (preventAutoLogin || sessionPreventAutoLogin) {
        console.log('AuthContext: Auto-login prevented by flag, skipping all auth checks');
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      const token = getAuthToken();
      const csrfToken = getCsrfTokenSync();
      const csrfTokenKey = getCsrfTokenKeyByEnv();
      console.log('AuthContext: CSRF Token', csrfToken);
      
      console.log('AuthContext: Checking auth status', { 
        hasToken: !!token, 
        hasCsrfToken: !!csrfToken,
        tokenLength: token ? token.length : 0,
        preventAutoLogin,
        sessionPreventAutoLogin
      });
      
      if (!token) {
        console.log('AuthContext: No token found, setting as not authenticated');
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      // Valid access token present — treat as authenticated until session check completes
      console.log('AuthContext: Setting as authenticated initially');
      setIsAuthenticated(true);

      // Try to validate the session with the backend via GraphQL
      try {
        console.log('AuthContext: Validating session with backend (GraphQL checkSession)');
        const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(csrfToken ? { [csrfTokenKey]: csrfToken } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            query: `query CheckSession {
              checkSession {
                status
                statusCode
                message
                errorCode
                data { mid brandName }
              }
            }`,
            variables: {},
          }),
        });
        // Pick up any rotated CSRF token returned by the server
        extractAndStoreCsrfToken(response);

        let json = null;
        try {
          json = await response.json();
        } catch {
          json = null;
        }
        const checkSession = json?.data?.checkSession;
        const gqlErrors = json?.errors;
        const gqlStatusCode = gqlErrors?.[0]?.extensions?.statusCode;
        const sessionStatusCode = checkSession?.statusCode;
        // Backend may report auth failures via either GraphQL `errors[]` or
        // inside the `checkSession` payload. Treat both the same.
        const effectiveStatusCode = gqlStatusCode ?? sessionStatusCode;

        console.log('AuthContext: Session validation response', {
          httpStatus: response.status,
          gqlStatusCode,
          sessionStatusCode,
          checkSessionStatus: checkSession?.status,
        });

        // 401 from the HTTP layer or in the GraphQL payload → unauthorized
        if (response.status === STATUS.UNAUTHORIZED || effectiveStatusCode == STATUS.UNAUTHORIZED) {
          console.log('AuthContext: Session unauthorized (401), clearing tokens');
          clearAuthState();
          return;
        }

        // 498 → access token expired; try once to refresh and re-check.
        if (effectiveStatusCode == STATUS.EXPIRED) {
          if (!isRetryAfterTokenRefresh) {
            console.log('AuthContext: Access token expired (498), attempting refresh');
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              await checkAuthStatus(true);
              return;
            }
          }
          console.log('AuthContext: Refresh failed or already retried, clearing tokens');
          clearAuthState();
          return;
        }

        if (checkSession?.status === STATUS.OK) {
          console.log('AuthContext: Session is valid, keeping authenticated');
          setIsAuthenticated(true);
        } else {
          // Grievance email OTP tokens may not return OK on this endpoint; keep the client session
          console.warn('AuthContext: checkSession body not OK; keeping token (email / alternate session)');
          setIsAuthenticated(true);
        }
      } catch (sessionError) {
        console.error('AuthContext: Session validation failed:', sessionError);
        // If session validation fails but we still have a token, keep the user signed in
        // to avoid logout on network issues or temporary backend problems
        if (token) {
          console.log('AuthContext: Keeping authenticated despite session validation failure');
          setIsAuthenticated(true);
        } else {
          // Clear invalid tokens
          console.log('AuthContext: Clearing tokens due to validation failure');
          clearAuthState();
        }
      }
    } catch (error) {
      console.error('AuthContext: Error checking auth status:', error);
      // Clear invalid tokens
      clearAuthState();
    } finally {
      console.log('AuthContext: Setting loading to false');
      setIsLoading(false);
    }
  };

  // Function to require authentication in major flows
  const requireAuth = () => {
    const accessToken = localStorage.getItem("accessToken") || "";
    if (!accessToken) {
      redirectToLogin();
      return false;
    }
    return true;
  };

  useEffect(() => {
    // Check if auto-login is prevented - if so, completely skip all auth logic
    const preventAutoLogin = localStorage.getItem('preventAutoLogin') === 'true';
    const sessionPreventAutoLogin = sessionStorage.getItem('preventAutoLogin') === 'true';
    
    if (preventAutoLogin || sessionPreventAutoLogin) {
      console.log('AuthContext: Auto-login prevented by flag, completely skipping all auth logic');
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    
    // Only proceed with auth checks if prevent flags are not set
    console.log('AuthContext: No prevent flags set, proceeding with auth checks');
    
    // Check authentication status on app load
    checkAuthStatus();
  }, []);

  // Debug effect to log state changes
  useEffect(() => {
    console.log('AuthContext: State changed', { isAuthenticated, isLoading });
  }, [isAuthenticated, isLoading]);

  const contextValue = {
    isAuthenticated,
    isLoading,
    setIsAuthenticated,
    clearAuthState,
    logoutUser,
    requireAuth
  };

  return (
    <AuthContext.Provider
      value={contextValue}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 